import clsx from 'clsx';
import { useState, useEffect, useMemo } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

export function ExampleTable() {

    const columns = [
        { key: 'PatientID', label: 'Patient ID', hideable: false, sortable: true, defaultVisible: true, filterable: false, width: 'w-24' },
        { key: 'PatientName', label: 'Patient Name', hideable: true, sortable: true, defaultVisible: false, filterable: false, width: 'w-1/4' },
        { key: 'StudyDescription', label: 'Study Description', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-1/4' },
        { key: 'Modality', label: 'Modality', hideable: true, sortable: true, defaultVisible: true, filterable: true, width: 'w-20' },
        { key: 'SeriesInstanceUID', label: 'Series Instance UID', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-1/3' },
        { key: 'NumberOfFiles', label: 'Number of Files', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-20' },
        { key: 'TotalSize', label: 'Total Size', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-20', display: value => `${(value / (1024 * 1024)).toFixed(2)} MB` },
        { key: 'AvailableLocally', label: 'Available Locally', hideable: true, sortable: true, defaultVisible: true, filterable: true, width: 'w-20', display: (value => value ? 'Yes' : 'No') },
        { key: 'ImportDate', label: 'Import Date', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-32' },
        { key: 'Tasks', label: 'Tasks', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-20' },
        { key: 'Actions', label: 'Actions', hideable: false, sortable: false, defaultVisible: true, filterable: false, width: 'w-32' }
    ];

    const instnces = [
        { 
            SeriesInstanceUID: '1.2.3a',
            PatientID: '5555',
            PatientName: 'Peter Doe',
            StudyDescription: 'Sample Study',
            Modality: 'CT',
            NumberOfFiles: 5,
            TotalSize: 123456789,
            AvailableLocally: true,
            ImportDate: '2023-10-01',
            Tasks: 'None',
            Actions: <button className="text-blue-500 hover:underline">View</button>,
            selected: true
        },
         { 
            SeriesInstanceUID: '1.2.3b',
            PatientID: '12345',
            PatientName: 'John Doe',
            StudyDescription: 'AAAAA',
            Modality: 'MR',
            NumberOfFiles: 5,
            TotalSize: 123456789,
            AvailableLocally: true,
            ImportDate: '2023-10-01',
            Tasks: 'None',
            Actions: <button className="text-blue-500 hover:underline">View</button>,
            selected: false
        },
        { 
            SeriesInstanceUID: '1.2.3c',
            PatientID: '67890',
            PatientName: 'Jane Smith',
            StudyDescription: 'BBBBB Study',
            Modality: 'CT',
            NumberOfFiles: 5,
            TotalSize: 123456789,
            AvailableLocally: false,
            ImportDate: '2023-10-01',
            Tasks: 'None',
            Actions: <button className="text-blue-500 hover:underline">View</button>,
            selected: true
        }
    ]

    const options = [
        {
            label: 'Clear Table',
            className: 'text-red-500 hover:text-red-700',
            onClick: () => {
                console.log('Clear Table clicked');
                // Implement clear table logic here
            }
        }
    ];

    const [rows, setRows] = useState(instnces);

    const onSelect = (row) => {
        console.log('Row selected:', row);
        setRows(prevRows =>
            prevRows.map(r => 
                r.SeriesInstanceUID === row.SeriesInstanceUID ? { ...r, selected: !r.selected } : r
            )
        );
    };

    const onDrop = (items) => {
        console.log('Files dropped:', items);
    }

    const summary = (filteredRows) => {
        const totalSize = filteredRows.reduce((sum, row) => sum + (row.TotalSize || 0), 0) / (1024 * 1024 * 1024); // Convert to GB
        return (
            <div className="flex items-center gap-2 flex-grow px-5">
                <div className="text-sm text-gray-700">
                    Total: {filteredRows.length} instance{filteredRows.length === 1 ? '' : 's'}
                </div>
                <div className="text-sm text-gray-700">
                    Size: {totalSize.toFixed(2)} GB
                </div>
            </div>
        );
    }

    return <Table name='example' rows={rows} columns={columns} onSelect={onSelect} onDrop={onDrop} options={options} summary={summary} />;
}

export function DateDisplay(value) {
    const date = new Date(value);

    return ( date ?
        <span title={date.toLocaleDateString()}>
            {String(date.getDate()).padStart(2, '0')}.{String(date.getMonth() + 1).padStart(2, '0')}.{date.getFullYear()}
        </span>
        :
        <span>-</span>
    );
}

function TableControls({ 
        columns, 
        searchQuery, setSearchQuery, 
        filters, filterOptions, setFilters,
        visibleColumns, setVisibleColumns, resetVisibleColumns,
        isSelectable, isAllSelected, handleSelectAll, 
        options,
        buttons
    }) {
   
    return (
        <div className="sticky top-0 z-20  flex items-center justify-between bg-gray-50 py-6">
            <div className="flex gap-4">
                <TableSearchInput searchQuery={searchQuery} onSearchChange={setSearchQuery} />
                {Object.keys(filterOptions).map((columnKey) => (
                    <TableFilterDropdown 
                        key={columnKey}
                        label={columnKey}
                        options={Array.from(filterOptions[columnKey] || [])}
                        selected={filters[columnKey]}
                        onChange={(filter) => setFilters(columnKey, filter)}
                    />
                ))}
            </div>
            <div className="flex items-center gap-2">
                { isSelectable && 
                    <TableControlsButton onClick={() => handleSelectAll(!isAllSelected)} >
                        {isAllSelected ? "Unselect All" : "Select All"}
                    </TableControlsButton> 
                }
                <TableVisibleColumnMenu
                    columns={columns}
                    visibleColumns={visibleColumns}
                    onChange={(colKey, isVisible, updatedVisibleColumns) => {
                        setVisibleColumns(updatedVisibleColumns);
                    }}
                    onReset={() => resetVisibleColumns()}
                />
                { options && (options.length > 0) && <TableOptionsMenu options={options} />}
                { buttons }
            </div>
        </div>
    );
}

export function TableSearchInput({ searchQuery, onSearchChange }) {
    return (
        <input
            type="text"
            placeholder="Search..."
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
        />
    );
}

function TableFilterDropdown({ label, options, selected, onChange }) {
    return (
        <select
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            value={selected || ''}
            onChange={(e) => onChange(e.target.value)}
        >
            <option value="">{label}: All</option>
            {options.map((option) => {
                let display = option;
                if (typeof option === 'boolean') {
                    display = option ? "True" : "False";
                }
                return (
                    <option key={String(option)} value={option}>
                        {label}: {display}
                    </option>
                );
            })}
        </select>
    );
}

export function TableControlsButton({ children, onClick, disabled = false, className}) {
    return (
        <button
            className={clsx(
                "px-3 py-2 text-sm rounded text-gray-800 bg-gray-200 hover:bg-gray-300 font-semibold",
                "disabled:opacity-50 disabled:cursor-default",
                className
            )}
            onClick={onClick}
            disabled={disabled}
        >
            { children }
        </button>
    );
}

function TableMenu({ label, children }) {
    return (
        <Menu as="div" className="relative inline-block text-left">
            <MenuButton className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300">
                { label }
                <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </MenuButton>
            <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
                { children }
            </MenuItems>
        </Menu>
    );
}

function TableVisibleColumnMenu({ columns, visibleColumns, onChange, onReset }) {
    // const columns = [
    //     { key: 'id', label: 'ID', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-20' },
    //     { key: 'name', label: 'Name', hideable: true, sortable: true, defaultVisible: true, filterable: true, width: 'w-1/3' },
    //     { key: 'status', label: 'Status', hideable: true, sortable: true, defaultVisible: true, filterable: true, width: 'w-1/4' },
    //     { key: 'actions', label: 'Actions', hideable: false, sortable: false, defaultVisible: true, filterable: false, width: 'w-1/4' }
    // ];

    const onColumnItemClick = (column) => {
        if (column.hideable) {
            const updatedVisibleColumns = { ...visibleColumns };
            updatedVisibleColumns[column.key] = !updatedVisibleColumns[column.key];
            onChange(column.key, !visibleColumns[column.key], updatedVisibleColumns);
        }
    }

    return (
        <TableMenu label="Columns">
            { columns.map((column) => 
                <MenuItem key={column.key} as="div">
                    <div
                        className={clsx(
                            'flex items-center justify-between px-4 py-2 text-sm cursor-pointer',
                            column.hideable ? 'hover:bg-gray-100' : 'opacity-50 cursor-default'
                        )}
                        onClick={e => {
                            e.preventDefault();
                            onColumnItemClick(column)
                        }}
                    >
                        <span>{ column.label }</span>
                        { visibleColumns[column.key] && <CheckIcon className="h-4 w-4 text-sky-500" aria-hidden="true" /> }
                    </div>
                </MenuItem>
            ) }
            { onReset && 
                <MenuItem as="div">
                    <div
                        className="px-4 py-2 text-sm text-red-600 border-t cursor-pointer hover:bg-gray-100 rounded-b-md"
                        onClick={e => {
                            e.preventDefault();
                            onReset();
                        }}
                    >
                        Reset to Default
                    </div>
                </MenuItem>
            }
        </TableMenu>
    );
}

function TableOptionsMenu({ options }) {

    const Radio = ({checked}) => (
        <span className={clsx(
            "w-2 h-2 line rounded-full border-2 border-gray-600",
            checked ? "bg-gray-600" : "bg-white"
        )} />
    );

    return (
        <TableMenu label="Options">
            {options.map((option) => (
                <MenuItem as="div">
                    <div 
                        className={clsx(
                            "items-center justify-between px-4 py-2 text-sm text-gray-700 flex justify-between", 
                            option.disabled ? "opacity-50 cursor-default" : "cursor-pointer hover:bg-gray-100",
                            option.className
                        )} 
                        onClick={() => {
                            if (!option.disabled && option.onClick) {
                                option.onClick();
                            }
                        }}
                    >
                        {option.label}
                        {option.checked !== undefined && <Radio checked={option.checked} />}
                    </div>
                </MenuItem>
            ))}
        </TableMenu>
    );
}


function TableHeader({ columns, onSort, sortBy, sortDir }) {
    // TBD
}

function TableBody({ data, columns, onSelect }) {
    const [isDragOver, setIsDragOver] = useState(false);
    // TBD
}

function TableColumnHeader({ children, isActionable, className, onClick }) {
    return (
        <th
            className={clsx(
                "px-3 py-3.5 text-left text-sm font-semibold transition-colors duration-150",
                "first:rounded-tl-lg first:border-l border-t border-gray-200 last:border-r last:rounded-tr-lg border-b",
                "text-gray-900 select-none group gap-2",
                isActionable ? "hover:bg-gray-100 cursor-pointer" : "cursor-default",
                className
            )}
            onClick={onClick}
            scope="col"
        >
            { children }
        </th>
    );
}

function SortableColumnHeader ({ label, sortKey, sortDir, isSortable, isActive, handleSort }) {
    return (
        <TableColumnHeader
            isActionable={!isActive && isSortable}
            className={isActive && "cursor-pointer bg-sky-50"}
            onClick={() => isSortable && handleSort(sortKey)}
        >
            <span className="whitespace-nowrap flex items-center gap-2 justify-between relative pr-5">  
                {label}
                {isActive && (
                    <svg
                        className="w-4 h-4 text-sky-600 inline-block absolute -right-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                        style={{ display: 'inline', verticalAlign: 'middle' }}
                    >
                        {sortDir === 'asc' ? (
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        )}
                    </svg>
                )}
            </span>
        </TableColumnHeader>
    );
}

function SelectableColumnHeader(onClick) {
    return (
        <TableColumnHeader onClick={onClick}>
            Select
        </TableColumnHeader>
    );
}

export function Table({ 
    name, rows, columns, options = [], buttons, side,
    onSelect, onDrop,
    summary = null, highlight = null, highlightClassName = "bg-yellow-100",
    dropMessage = "Drop files here.", emptyMessage = "No data available.", 
    pageSizeOptions = [10, 25, 50, 100] 
}) {
    
    // if name is null, set name to tbl-{hash of columns}
    name = 'tbl-' + (name ? name : btoa(columns.map(c => c.key).join('-')).replace(/=+$/, ''));
    console.info('Table name:', name);

    //
    const isSelectable = onSelect && typeof onSelect === 'function';
    const isDroppable = onDrop && typeof onDrop === 'function';

    // constants, keys, ..
    const lsVisibleColumnsKey = `${name}-visibleColumns`;
    const lsPaginationKey = `${name}-pagination`;

    // CZSTOM STATES, WILL BE MOVED OR GENERALIZED
    const [isDragOver, setIsDragOver] = useState(false);

    // State for search, sorting, pagination
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState(''); // column key
    const [sortDir, setSortDir] = useState('asc'); // 'asc' or 'desc'

    // **** Visible Columns
    const [visibleColumns, setVisibleColumns] = useState(() => {
        let stored_json = {};
        try {
            const stored_plain = localStorage.getItem(lsVisibleColumnsKey);
            if (stored_plain) {
                stored_json = JSON.parse(stored_plain);
            }
        } catch {}
        return columns.reduce((acc, col) => {
            if (col.key in stored_json) {
                acc[col.key] = stored_json[col.key];
            } else {
                acc[col.key] = col.defaultVisible === undefined ? true : col.defaultVisible;
            }
            return acc;
        }, {});
    });

    useEffect(() => {
        try {
            localStorage.setItem(lsVisibleColumnsKey, JSON.stringify(visibleColumns));
        } catch {}
    }, [visibleColumns]);

    const resetVisibleColumns = () => {
        const defaultVisible = columns.reduce((acc, col) => {
            acc[col.key] = col.defaultVisible === undefined ? true : col.defaultVisible;
            return acc;
        }, {});
        setVisibleColumns(defaultVisible);
    }
 
    // **** Pagination

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        try {
            const stored = localStorage.getItem(lsPaginationKey);
            if (stored) {
                const { pageSize } = JSON.parse(stored);
                return pageSize || 10;
            }
        }
        catch {}
        return 10;
    });

    useEffect(() => {
        try {
            localStorage.setItem(lsPaginationKey, JSON.stringify({ pageSize }));
        } catch {}
    }, [pageSize]);

    // **** Filters
    const filterableColumns = columns.filter(c => c.filterable);
    const filterOptions = filterableColumns.reduce((acc, curr) => {
        acc[curr.key] = new Set(rows.map(r => r[curr.key]));
        return acc;
    }, {});
    const [filters, setFilters] = useState(filterableColumns.reduce((acc, curr) => {
        acc[curr.key] = null;
        return acc;
    }, {}));

    // *** Instance Preparation (filter, sort, pagination)

    // 2. Comparator factory
    const sort_comparator = (a, b) => {

        const column = columns.find(c => c.key === sortBy);

        const va = (column && column.repr && typeof column.repr === 'function') ? column.repr(a[sortBy], a, column) : a[sortBy];
        const vb = (column && column.repr && typeof column.repr === 'function') ? column.repr(b[sortBy], b, column) : b[sortBy];

        if (typeof va === 'string' && typeof vb === 'string') {
            return va.localeCompare(vb) * (sortDir === 'asc' ? 1 : -1);
        }

        return (va < vb ? -1 : va > vb ? 1 : 0) * (sortDir === 'asc' ? 1 : -1);
    };

    // 3. Filter predicate
    const filter_predicate = (instance) => {

        // selection filter
        // NOTE: if filter or instance value is null, skip this filter
        for (const colKey in filters) {
            const filterValue = filters[colKey];
            if (filterValue && instance[colKey] && instance[colKey] !== filterValue) {
                return false; // If any filter doesn't match, exclude this instance
            }
        }
    
        // search query filter
        // TODO: make included columns configurable
        const q = searchQuery.toLowerCase();
        return q.length <= 0 || Object.values(instance).some((v) =>
            String(v).toLowerCase().includes(q)
        );
    };

    // 4. Combined memoized filter + sort
    const filteredRows = useMemo(() => {
        let arr = rows.filter(filter_predicate);
        if (sortBy) {
            arr = arr.slice().sort(sort_comparator);
        }
        return arr;
    }, [rows, searchQuery, filters, sortBy, sortDir]);

    // const filteredRows = rows.filter(filter_predicate).sort(sort_comparator);

    const handleSort = (colKey) => {
        if (sortBy === colKey) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(colKey);
            setSortDir('asc');
        }
    };

    // *** Pagination
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => {
        if (page > totalPages) setPage(1);
    }, [filteredRows.length, page, totalPages]);


    // Select/Unselect All logic
    const allFilteredSelected = filteredRows.length > 0 && filteredRows.every(i => i.selected);
    const handleSelectAll = (checked) => {
        filteredRows.forEach(row => {
            if (!!row.selected !== checked) {
                onSelect(row);
            }
        });
    };

    const handleSetFilters = (columnKey, filterValue) => {
        setFilters((prev) => ({
            ...prev,
            [columnKey]: filterValue,
        }));
    };

    return (
        <div className="px-8 flow-root w-full">
            {/* Header with search, modality filter, column toggle, and select/unselect all */}
            <TableControls
                columns={columns}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                filters={filters}
                filterOptions={filterOptions}
                setFilters={handleSetFilters}
                visibleColumns={visibleColumns}
                isSelectable={isSelectable}
                setVisibleColumns={setVisibleColumns}
                resetVisibleColumns={resetVisibleColumns}
                isAllSelected={allFilteredSelected}
                handleSelectAll={handleSelectAll}
                options={options}
                buttons={buttons}
            />

            { /* split view */}
            <div className="flex sm:-mx-6 lg:-mx-8 py-2 sm:px-6 lg:px-8 gap-5">

                {/* Table view & Dropzone for drag-and-drop file upload */}
                <div
                    className="flex-grow relative p-1 -m-1"
                    onDragOver={(e) => {
                        if (!isDroppable) return;
                        e.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                        if (!isDroppable) return;
                        e.preventDefault();
                        setIsDragOver(false);
                        onDrop(e.dataTransfer.items);
                    }}
                >
                    {isDroppable && isDragOver && (
                        <div className="absolute inset-0 bg-blue-100 bg-opacity-75 flex items-center justify-center z-30 sm:rounded-lg">
                            <p className="text-lg font-semibold text-blue-600">
                                { dropMessage }
                            </p>
                        </div>
                    )}
                    <div className="min-w-full align-middle">
                        <div className="sm:rounded-lg">
                            <table className="min-w-full border-separate border-spacing-0">
                                <thead className="sticky top-[86px] left-{200px} bg-gray-50">
                                    <tr className="">
                                        { isSelectable &&
                                            <SortableColumnHeader
                                                key="selected"
                                                label="Select"
                                                isSortable={true}
                                                isActive={sortBy === "selected"}
                                                sortKey="selected"
                                                sortDir={sortDir}
                                                handleSort={handleSort}
                                            />
                                        }

                                        { columns.map((col) => (
                                            visibleColumns[col.key] && (
                                                <SortableColumnHeader
                                                    key={col.key}
                                                    label={col.label}
                                                    isSortable={col.sortable}
                                                    isActive={sortBy === col.key}
                                                    sortKey={col.key}
                                                    sortDir={sortDir}
                                                    handleSort={handleSort}
                                                />
                                            )
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className=" bg-white">
                                    {paginatedRows.length > 0 ? (
                                        paginatedRows.map((row, index) => {
                                            const isDisabled = typeof row.disabled === 'boolean' ? row.disabled : false;
                                            return (
                                                <tr key={index} 
                                                    className={clsx(
                                                        "hover:bg-gray-50", 
                                                        isSelectable && !isDisabled && "cursor-pointer", 
                                                        isDisabled && "opacity-50",
                                                        highlight && (typeof highlight === "function") && highlight(row) && highlightClassName
                                                    )}
                                                    onClick={() => isSelectable && !isDisabled && onSelect(row)}
                                                >
                                                    { isSelectable && 
                                                        <td className={clsx(
                                                                "border-b border-gray-200 border-b-gray-100 first:border-l last:border-r",
                                                                "whitespace-nowrap px-3 py-2 text-sm text-gray-500"
                                                            )}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!row.selected}
                                                                readOnly
                                                            />
                                                        </td>
                                                    }
                                                    { columns.map((col) => {
                                                        
                                                        const value = col.repr ? col.repr(row[col.key], row, col) : row[col.key];
                                                        const display = col.display ? col.display(value, row, col) : value;

                                                        return visibleColumns[col.key] && (
                                                            <td className={clsx(
                                                                "border-b border-b-gray-100 border-gray-200 first:border-l last:border-r",
                                                                "whitespace-nowrap px-3 py-2 text-sm text-gray-500",
                                                                col.width || ''
                                                            )} key={col.key}>
                                                                { display }
                                                            </td>
                                                        )    
                                                    })}
                                                </tr>
                                            )
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={columns.length + (isSelectable ? 1 : 0)} className="p-4 text-center text-gray-500 border-l border-r border-b">
                                                { emptyMessage }
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination controls */}
                            <div className={clsx(
                                "flex items-center justify-between px-4 py-2 bg-gray-50 border-r border-b border-l border-gray-200 rounded-b-lg",
                                "sticky bottom-5 z-10 after:absolute after:bg-gray-50 after:inset-0 after:z-10 after:top-11 after:h-10 after:mt-[1px] after:-mx-1"
                            )}>
                                <div className="flex items-center gap-4">
                                    <div>
                                        <label className="mr-2 text-sm text-gray-700">Rows per page:</label>
                                        <select
                                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                                            value={pageSize}
                                            onChange={e => {
                                                setPageSize(Number(e.target.value));
                                                setPage(1);
                                            }}
                                        >
                                            {pageSizeOptions.map(size => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                { summary && typeof summary === 'function' && summary(filteredRows) }
                                <div className="flex items-center gap-2">
                                    <button
                                        className="px-2 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                    >
                                        Prev
                                    </button>
                                    <span className="text-sm text-gray-700">
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        className="px-2 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                { /* side view */ }
                { side &&
                    <div className="sticky top-[86px] h-min border-gray-200 rounded-lg">
                        {side}
                    </div>
                }
            </div>
        </div>
    );
}